// <SyqiHome/> — the SyQi ORDER dashboard, five stacked colored bands
// (Orient / Resources / Directions / Execute / Revise). Each band hosts the
// embedded widgets of the tools the user has enabled for that section.
//
// Layout: overall ORDER score card at top, ORACLE spine banner, then five
// collapsible sections with per-tool scores and live in-place ToolHost widgets.
//
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { GlassCard } from '../v10/ui';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { Gear, GEARS, TOOL_NAMES } from '../domain/olmps';
import { supabase } from '../lib/supabase';
import { useSharedTodayData } from './TodayDataContext';
import type { StepItem } from './StepScreen';
import { loadSpine } from './spine-data';
import { nextSpineStep, type SpineStep } from './spine-types';
import { useSyqiSections, ORDER_SECTIONS, ORDER_SECTION_META, enabledToolsFor } from './orderSections';
import { computeOrderScores, formatScore } from './orderScores';
import { getOrderWidget } from './orderWidgets';
import { ClarifyBox } from './orderWidgets/ClarifyBox';
import { clarifyQuestionFor, engagementModeFor, markToolEngaged, useToolEngagement } from './toolEngagement';
import { SyqiOverview } from './SyqiOverview';
import { SectionHelpPopup } from './SectionHelpPopup';
import { DeityPlacard } from './DeityPlacard';

/** One tool's daily engagement light: red until the user has engaged with that
 *  tool today, green after. Dark ring so it reads on any metal band. */
function EngagementDot({ engaged, size = 14 }: { engaged: boolean; size?: number }) {
  const { t } = useTheme();
  const styles = useStyles();
  return (
    <View
      style={[
        styles.dot,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: engaged ? t.onPace : t.lapsed,
        },
      ]}
    />
  );
}

const SPINE_STEP_COPY: Partial<Record<SpineStep, string>> = {
  telos: "Set your sectors' direction",
  moyri: 'Place your lines',
  kyrs: 'Load your tasks',
};

export function SyqiHome({
  userId,
  onOpenCompass,
  onOpenTool,
  onOpenStep,
  onOpenSession,
  onOpenSpine,
}: {
  userId: string | undefined;
  onOpenCompass: () => void;
  onOpenTool: (toolId: string, gear?: Gear, opts?: { initialTaskId?: string }) => void;
  onOpenStep: (item: StepItem) => void;
  onOpenSession: (kind: 'morning' | 'night') => void;
  onOpenSpine?: () => void;
}) {
  const styles = useStyles();
  const data = useSharedTodayData();
  const { sections } = useSyqiSections(userId);
  const scores = computeOrderScores(sections, data);
  const engagement = useToolEngagement(userId, data);

  const [now, setNow] = useState(() => new Date());
  const [spineStep, setSpineStep] = useState<SpineStep | null>(null);
  // Which section's help popup is open (one popup serves all five bands).
  const [helpGear, setHelpGear] = useState<Gear | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<Gear, boolean>>(() =>
    Object.fromEntries(GEARS.map((gear) => [gear, false])) as Record<Gear, boolean>
  );

  // Keep "now" fresh for time-based zoning.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  // "Set up your OLMPS" spine banner — same as the previous ORACLE dashboard.
  useEffect(() => {
    if (!onOpenSpine || !userId) {
      setSpineStep(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const spine = await loadSpine(supabase, userId);
      if (!cancelled) setSpineStep(nextSpineStep(spine));
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, onOpenSpine]);

  // No full-page loading gate — the ORDER card and section bands render
  // immediately and scores fill in as each of useTodayData's queries lands
  // (formatScore shows "–" until a signal exists). See useTodayData's
  // progressive-load note.
  const toggleSection = (gear: Gear) => {
    setExpandedSections((prev) => ({ ...prev, [gear]: !prev[gear] }));
  };

  return (
    <View style={styles.root}>
      <ScrollView contentContainerStyle={styles.container}>
        {/* SyQi daily overview */}
        <SyqiOverview userId={userId} />

        {/* ORACLE spine banner */}
        {onOpenSpine && spineStep && spineStep !== 'done' && (
          <Pressable onPress={onOpenSpine}>
            <GlassCard style={styles.spineCard}>
            <View style={{ flex: 1 }}>
              <Text style={styles.spineTitle}>ORACLE — set your future</Text>
              <Text style={styles.spineSubtitle}>{SPINE_STEP_COPY[spineStep] ?? 'Continue setup'}</Text>
            </View>
            <Text style={styles.linkChevron}>›</Text>
            </GlassCard>
          </Pressable>
        )}

        {/* Five ORDER sections — one connected block (only outer corners round) */}
        <GlassCard style={styles.sectionStack}>
        {ORDER_SECTIONS.map((gear) => {
          const meta = ORDER_SECTION_META[gear];
          const isExpanded = expandedSections[gear];
          const tools = enabledToolsFor(sections, gear);
          const sectionScore = scores.bySection[gear];

          return (
            <View key={gear} style={styles.sectionOuter}>
              {/* Section header band — glass strip with a small sector-metal
                  hairline on its left edge. */}
              <Pressable onPress={() => toggleSection(gear)} style={styles.sectionHeader}>
                <View style={[styles.sectionAccent, { backgroundColor: meta.color }]} pointerEvents="none" />
                <Text style={styles.sectionHeaderText}>
                  {meta.title.toUpperCase()}: {formatScore(sectionScore.score)}
                </Text>
                {/* One engagement light per active tool, in section order —
                    the day's goal is every dot in every band green. */}
                <View style={styles.dotRow} pointerEvents="none">
                  {tools.map((toolId) => (
                    <EngagementDot key={toolId} engaged={engagement.isEngaged(gear, toolId)} />
                  ))}
                </View>
                <Text style={styles.sectionChevron}>{isExpanded ? '▾' : '◂'}</Text>
              </Pressable>

              {/* Expanded body with tools */}
              {isExpanded && (
                <View style={styles.sectionBody}>
                  {tools.length === 0 ? (
                    <Text style={styles.noToolsText}>
                      No tools active — toggle them in Settings → SyQi.
                    </Text>
                  ) : (
                    tools.map((toolId) => {
                      const Widget = getOrderWidget(toolId);
                      const engaged = engagement.isEngaged(gear, toolId);
                      const question = clarifyQuestionFor(toolId, data);
                      return (
                        <View key={toolId} style={styles.toolEntry}>
                          {/* Deity name plate — the one title treatment every
                              deity gets, app-wide (see DeityPlacard). */}
                          <View style={styles.toolLabelRow}>
                            <EngagementDot engaged={engaged} size={11} />
                            <DeityPlacard toolId={toolId} onPress={() => onOpenTool(toolId, gear)} />
                          </View>
                          <Widget
                            userId={userId}
                            gear={gear}
                            data={data}
                            onOpen={() => onOpenTool(toolId, gear)}
                            onOpenTool={(id, opts) => onOpenTool(id, gear, opts)}
                            onEngage={() => void markToolEngaged(userId, gear, toolId)}
                          />
                          {/* Observational tools have nothing to submit, so SyQi
                              asks one question about their state — answering it
                              is the engagement. */}
                          {engagementModeFor(toolId) === 'clarify' && (
                            <ClarifyBox
                              question={question}
                              engagement={engagement.engagementFor(gear, toolId)}
                              onSubmit={(note) => engagement.submitClarify(gear, toolId, question, note)}
                            />
                          )}
                        </View>
                      );
                    })
                  )}
                  {/* Section help — how to fill this section out, plus a box to
                      ask SyQi about it. Floats in the body's top-right corner;
                      declared last so it paints (and hit-tests) above the
                      widgets it sits over. */}
                  <Pressable
                    style={styles.helpButton}
                    onPress={() => setHelpGear(gear)}
                    hitSlop={8}
                  >
                    <Text style={styles.helpButtonText}>?</Text>
                  </Pressable>
                </View>
              )}
            </View>
          );
        })}
        </GlassCard>
      </ScrollView>

      {/* Mounted only while open — the popup owns a chat hook, and there is no
          reason for five idle sections to keep one alive. */}
      {helpGear && (
        <SectionHelpPopup
          gear={helpGear}
          tools={enabledToolsFor(sections, helpGear)}
          onClose={() => setHelpGear(null)}
        />
      )}
    </View>
  );
}

/** Band header height — fixed so the absolutely-centred title can use it as its
 *  lineHeight and sit dead centre in every band. */
const BAND_H = 56;

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1 },
    container: { padding: 16, gap: 12, paddingBottom: 8 },

    spineCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      borderRadius: radii.panel,
      borderColor: t.gold,
      boxShadow: `0 6px 18px ${t.goldGlow}`,
      paddingHorizontal: 16,
      paddingVertical: 18,
    },
    spineTitle: { fontSize: 16, letterSpacing: 2.5, color: t.goldBright, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    spineSubtitle: { fontSize: 12, color: t.ivorySoft, marginTop: 3, fontWeight: '600' },
    linkChevron: { fontSize: 24, fontWeight: '700', color: t.goldBright },

    // One connected block: parent clips the outer corners, bands sit flush.
    sectionStack: { borderRadius: radii.panel, padding: 0 },
    sectionOuter: { borderTopWidth: 1, borderTopColor: t.lineFaint },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingHorizontal: 16,
      height: BAND_H,
    },
    /** Small sector-metal hairline on the band's left edge. */
    sectionAccent: { position: 'absolute', top: 14, bottom: 14, left: 0, width: 3, borderRadius: 2 },
    /** Spans the whole band so every title shares the same centre line, whatever
        number of engagement dots the band carries. */
    sectionHeaderText: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      fontSize: 17,
      lineHeight: BAND_H,
      letterSpacing: 2.5,
      textAlign: 'center',
      color: t.ivory,
      fontFamily: DISPLAY_FONT,
      textTransform: 'uppercase',
    },
    sectionChevron: { fontSize: 16, fontWeight: '700', marginLeft: 8, color: t.ivorySoft },

    sectionBody: {
      paddingHorizontal: 10,
      paddingVertical: 10,
      gap: 10,
    },

    // "?" help affordance, floating over the body's top-right corner so it never
    // pushes the widgets around. zIndex keeps it above any widget canvas.
    helpButton: {
      position: 'absolute',
      top: 8,
      right: 10,
      zIndex: 10,
      width: 26,
      height: 26,
      borderRadius: 13,
      borderWidth: 1,
      borderColor: t.gold,
      backgroundColor: t.glassStrong,
      alignItems: 'center',
      justifyContent: 'center',
    },
    helpButtonText: { fontSize: 15, fontWeight: '900', lineHeight: 18, color: t.goldBright },

    // Layout only. Each widget paints its own surface — no card chrome here,
    // or every widget reads as a panel inside a redundant second panel.
    toolEntry: {
      gap: 8,
    },
    toolLabelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },

    // Engagement lights: night ring so they read on glass in both modes.
    dotRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 10 },
    dot: {
      borderWidth: 1.5,
      borderColor: t.night,
    },

    noToolsText: { fontSize: 13, fontStyle: 'italic', textAlign: 'center', paddingVertical: 8, color: t.ivoryFaint },
  }),
);

export default SyqiHome;
